import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { PERSONA_IMAGES, MEDDPICC_LABELS } from "@/lib/api";
import { 
  CheckCircle2, AlertCircle, ArrowRight, 
  TrendingUp, Target, Loader2, Users
} from "lucide-react";

import { getPersonaMeta } from "@/lib/personaCatalog";

const PERSONA_META = new Proxy({}, { get: (_, pid) => (typeof pid === "string" ? getPersonaMeta(pid) : undefined) });

export const InterimSummaryPanel = ({ 
  summary, 
  currentPersona, 
  dealState, 
  onSwitchPersona, 
  onContinue, 
  onEndSimulation, 
  loading 
}) => {
  const [switching, setSwitching] = useState(false);
  const current = getPersonaMeta(currentPersona);
  const recommended = summary?.recommended_next_persona;
  const recommendedMeta = PERSONA_META[recommended];

  const handleSwitch = async () => {
    if (!recommended || recommended === "none") return;
    setSwitching(true);
    await onSwitchPersona(recommended);
    setSwitching(false);
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-white">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-3" />
          <p className="text-sm text-slate-400">Generating interim summary...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto bg-white" data-testid="interim-summary-panel">
      <div className="max-w-xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Users className="w-7 h-7 text-primary" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-1">Conversation Pause</h2>
          <p className="text-sm text-slate-400">
            Your conversation with {current.name} has reached a natural stopping point.
          </p>
        </div>

        {/* Summary */}
        <Card className="mb-5 border-slate-200">
          <CardContent className="pt-5">
            <div className="flex items-center gap-3 mb-4">
              <Avatar className="w-10 h-10">
                <AvatarImage src={PERSONA_IMAGES[currentPersona]} />
                <AvatarFallback className="bg-slate-100 text-sm">{current.short}</AvatarFallback>
              </Avatar>
              <div>
                <p className="font-semibold text-slate-900 text-sm">{current.name}</p>
                <p className="text-xs text-slate-400">{current.role}</p>
              </div>
              <Badge variant="outline" className="ml-auto text-xs bg-slate-50">
                Trust: {dealState?.trust_score}/100
              </Badge>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed mb-4" data-testid="interim-summary-text">
              {summary?.summary}
            </p>
            
            {/* Key Wins & Gaps */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-semibold text-emerald-600 mb-2 uppercase tracking-wide">Key Wins</p>
                <ul className="space-y-1.5">
                  {summary?.key_wins?.map((win, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-xs text-slate-600">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      {win}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-xs font-semibold text-amber-600 mb-2 uppercase tracking-wide">Gaps to Address</p>
                <ul className="space-y-1.5">
                  {summary?.gaps?.map((gap, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-xs text-slate-600">
                      <AlertCircle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                      {gap}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* MEDDPICC Mini-Progress */}
        <Card className="mb-5 border-slate-200">
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 mb-3">
              <Target className="w-4 h-4 text-primary" />
              <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">MEDDPICC Progress</p>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {Object.entries(dealState?.meddpicc_progress || {}).map(([key, value]) => {
                const percent = Math.round(value * 100);
                const info = MEDDPICC_LABELS[key];
                return (
                  <div key={key} className="text-center">
                    <div className="relative w-10 h-10 mx-auto mb-1">
                      <svg className="w-full h-full transform -rotate-90">
                        <circle cx="20" cy="20" r="16" stroke="#F1F5F9" strokeWidth="3" fill="none" />
                        <circle
                          cx="20" cy="20" r="16"
                          stroke={info?.color || "#64748B"}
                          strokeWidth="3"
                          fill="none"
                          strokeDasharray={`${percent * 1.005} 100.5`}
                          strokeLinecap="round"
                        />
                      </svg>
                      <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold" style={{ color: info?.color }}>
                        {key}
                      </span>
                    </div>
                    <span className="text-[10px] text-slate-400">{percent}%</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Recommendation */}
        {recommended && recommended !== "none" && recommendedMeta && (
          <Card className="mb-5 border-primary/30 bg-primary/5">
            <CardContent className="pt-5">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4 text-primary" />
                <p className="text-xs font-semibold text-primary uppercase tracking-wide">Recommended Next</p>
              </div>
              <div className="flex items-center gap-3 mb-3">
                <Avatar className="w-12 h-12 border-2 border-primary/30">
                  <AvatarImage src={PERSONA_IMAGES[recommended]} />
                  <AvatarFallback className="bg-slate-100">{recommendedMeta.short}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold text-slate-900">{recommendedMeta.name}</p>
                  <p className="text-xs text-slate-400">{recommendedMeta.role}</p>
                </div>
              </div>
              <p className="text-sm text-slate-600 mb-3" data-testid="recommendation-reason">
                {summary?.recommendation_reason}
              </p>
              {summary?.transition_message && (
                <div className="p-3 bg-white rounded-lg border border-slate-200 mb-3">
                  <p className="text-xs text-slate-400 mb-1">They'll start with:</p>
                  <p className="text-sm text-slate-600 italic">"{summary.transition_message}"</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <div className="space-y-3">
          {recommended && recommended !== "none" && (
            <Button 
              className="w-full bg-primary hover:bg-primary/90 font-semibold"
              size="lg"
              onClick={handleSwitch}
              disabled={switching}
              data-testid="switch-persona-btn"
            >
              {switching ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <ArrowRight className="w-4 h-4 mr-2" />
              )}
              Talk to {recommendedMeta?.name}
            </Button>
          )}
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="flex-1 border-slate-200"
              onClick={onContinue}
              data-testid="continue-current-btn"
            >
              Continue with {current.name}
            </Button>
            <Button 
              variant="outline" 
              className="flex-1 border-slate-200 text-slate-400"
              onClick={onEndSimulation}
              data-testid="end-from-interim-btn"
            >
              End Simulation
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
